import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function pad(n) { return String(n).padStart(2, "0"); }
function fmt(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

// Tailwind classes for appointment colors (must be static so Tailwind can JIT them)
const APPT_BG = {
  amber:   "bg-amber-500 hover:bg-amber-600",
  emerald: "bg-emerald-500 hover:bg-emerald-600",
  blue:    "bg-blue-500 hover:bg-blue-600",
  violet:  "bg-violet-500 hover:bg-violet-600",
  red:     "bg-red-500 hover:bg-red-600",
  stone:   "bg-stone-500 hover:bg-stone-600",
};

export default function CalendarPage() {
  const { t } = useI18n();
  const WEEKDAYS = t.weekdays_short || ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const MONTH_NAMES = t.months || ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDefaultDate, setDialogDefaultDate] = useState("");
  const [dialogExisting, setDialogExisting] = useState(null);
  const navigate = useNavigate();

  const range = useMemo(() => {
    const s = startOfMonth(cursor);
    const e = endOfMonth(cursor);
    return {
      start: fmt(new Date(s.getFullYear(), s.getMonth(), 1)),
      end: fmt(new Date(e.getFullYear(), e.getMonth(), e.getDate())),
    };
  }, [cursor]);

  const reload = useCallback(() => {
    api.get("/calendar/events", { params: range })
      .then(r => setEvents(r.data))
      .catch(() => setEvents([]));
  }, [range]);

  useEffect(() => { reload(); }, [reload]);

  const eventsByDay = useMemo(() => {
    const m = {};
    events.forEach(ev => {
      m[ev.date] = m[ev.date] || [];
      m[ev.date].push(ev);
    });
    // Sort each day: appointments with time first (chronologically), then others
    Object.keys(m).forEach(k => {
      m[k].sort((a, b) => {
        if (a.type === "appointment" && b.type === "appointment") {
          return (a.time || "").localeCompare(b.time || "");
        }
        if (a.type === "appointment") return -1;
        if (b.type === "appointment") return 1;
        return 0;
      });
    });
    return m;
  }, [events]);

  const first = startOfMonth(cursor);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = endOfMonth(cursor).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const today = fmt(new Date());

  const openNewAppointment = (dateStr) => {
    setDialogExisting(null);
    setDialogDefaultDate(dateStr || today);
    setDialogOpen(true);
  };
  const openExistingAppointment = (ev) => {
    setDialogExisting(ev);
    setDialogDefaultDate("");
    setDialogOpen(true);
  };

  const renderEventChip = (ev) => {
    if (ev.type === "appointment") {
      const bg = APPT_BG[ev.color] || APPT_BG.amber;
      return (
        <button
          key={ev.id}
          onClick={(e) => { e.stopPropagation(); openExistingAppointment(ev); }}
          className={`w-full text-left text-[10px] text-white px-1.5 py-0.5 truncate block rounded ${bg}`}
          data-testid={`cal-event-${ev.id}`}
          title={`${ev.time ? ev.time + " — " : ""}${ev.title}${ev.location ? " · " + ev.location : ""}`}
        >
          {ev.time && <span className="font-mono font-bold mr-1">{ev.time}</span>}
          {ev.title}
        </button>
      );
    }
    const colorClass = ev.type === "client_created" ? "bg-blue-600 hover:bg-blue-700"
      : ev.type === "policy_created" ? "bg-green-600 hover:bg-green-700"
      : ev.type === "renewal_reminder" ? "bg-red-600 hover:bg-red-700 ring-1 ring-red-700"
      : ev.type === "policy_expiry" ? "bg-red-700 hover:bg-red-800"
      : "bg-prime hover:bg-prime-dark";
    const prefix = ev.type === "client_created" ? "+"
      : ev.type === "policy_created" ? "$"
      : ev.type === "renewal_reminder" ? "⚡"
      : "!";
    return (
      <button
        key={ev.id}
        onClick={(e) => { e.stopPropagation(); if (ev.client_id) navigate(`/clients/${ev.client_id}`); }}
        className={`w-full text-left text-[10px] text-white px-1.5 py-0.5 truncate block ${colorClass}`}
        data-testid={`cal-event-${ev.id}`}
        title={ev.title}
      >
        <span className="font-bold mr-1">{prefix}</span>
        {ev.type === "client_created" ? ev.client_name : `${ev.company || ""} · ${ev.client_name || ""}`}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-prime">/ {t.calendar}</div>
          <h1 className="font-display font-extrabold text-5xl tracking-tight mt-1">
            {MONTH_NAMES[cursor.getMonth()]} <span className="text-muted-foreground">{cursor.getFullYear()}</span>
          </h1>
          <div className="flex items-center gap-4 mt-3 text-xs flex-wrap">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-500 rounded-sm inline-block"></span> Termin</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-600 inline-block"></span> Neuer Kunde</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-green-600 inline-block"></span> Neuer Vertrag</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-700 inline-block"></span> Ablauf Police</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            className="btn-prime gap-1.5"
            onClick={() => openNewAppointment(today)}
            data-testid="cal-new-appointment-btn"
          >
            <Plus className="w-4 h-4" /> Neuer Termin
          </Button>
          <Button variant="outline" className="rounded-none border-foreground" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" className="rounded-none border-foreground" onClick={() => setCursor(new Date())} data-testid="cal-today">{t.today || "Today"}</Button>
          <Button variant="outline" className="rounded-none border-foreground" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="prime-card">
        <div className="grid grid-cols-7 border-b border-border">
          {WEEKDAYS.map(w => (
            <div key={w} className="label-uppercase text-muted-foreground px-3 py-2 border-r border-border last:border-r-0">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            if (d == null) return <div key={i} className="min-h-28 bg-secondary/30 border-r border-b border-border last:border-r-0" />;
            const dateStr = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(d)}`;
            const evs = eventsByDay[dateStr] || [];
            const isToday = dateStr === today;
            return (
              <div
                key={i}
                className={`min-h-28 p-2 border-r border-b border-border last:border-r-0 hover:bg-secondary transition-colors cursor-pointer group relative ${isToday ? "bg-blue-50/40" : ""}`}
                data-testid={`cal-cell-${dateStr}`}
                onClick={() => openNewAppointment(dateStr)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") openNewAppointment(dateStr); }}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className={`text-xs font-mono ${isToday ? "text-prime font-bold" : ""}`}>{d}</div>
                  <Plus className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="space-y-1">
                  {evs.slice(0, 3).map(ev => renderEventChip(ev))}
                  {evs.length > 3 && (
                    <div className="text-[10px] text-muted-foreground">+{evs.length - 3}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AppointmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        defaultDate={dialogDefaultDate}
        existing={dialogExisting}
        onSaved={reload}
      />
    </div>
  );
}
